/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import WebGLContext from "App/web_gl_context";
import GLShader from  "App/render/webgl/renderables/shader";

import * as vsLogDepth from "App/shaders/log_depth/log_depth.vs";
import * as fsLogDepth from "App/shaders/log_depth/log_depth.fs";
import * as vsXray from "App/shaders/lighting/xray.vs";
import * as fsXray from "App/shaders/lighting/xray.fs";
import * as packToFromRGBA from "App/shaders/common/pack_to_from_rgba.vsfs";
import * as vsLightingCommon from "App/shaders/lighting/lighting_common.vs";
import * as fsLightingCommon from "App/shaders/lighting/lighting_common.fs";
import * as vsLightingInVS from "App/shaders/lighting/lighting_in_vs.vs";
import * as fsLightingInVS from "App/shaders/lighting/lighting_in_vs.fs";
import * as vsLighting from  "App/shaders/lighting/lighting.vs";
import * as fsLighting from  "App/shaders/lighting/lighting.fs";
import * as vsLlightingSimpleInVS from "App/shaders/lighting/lighting_simple_in_vs.vs";
import * as fsLightingSimpleInVS from "App/shaders/lighting/lighting_simple_in_vs.fs";
import * as vsLightingSimple from  "App/shaders/lighting/lighting_simple.vs";
import * as fsLightingSimple from  "App/shaders/lighting/lighting_simple.fs";
import * as vsCplane from "App/shaders/common/cplane.vs";
import * as fsCplane from "App/shaders/common/cplane.fs";
import IScene from "js_interface/src/scene_interface";

export default
class Shader {

    protected _sceneAPI: IScene;
    protected _renderGraph: WebGLRenderGraph;
    private _context: WebGLContext;
    protected _renderables: GLShader[];
    private _typeName: string;
    private _lightingType: number;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        this._sceneAPI = aSceneAPI;
        this._renderGraph = aRenderGraph;
        this._context = <WebGLContext>aRenderGraph.getContext();

        this._renderables = [];

        this._typeName = null;
        this._lightingType = 0; // 0 - none, 1 - simple, 2 - normal
    }

    public release() {
        for (let i = 0; i < this._renderables.length; i++) {
            if (this._renderables[i] !== null) {
                this._renderables[i].release();
            }
        }
        this._renderables = [];
    }

    public get(aIndex: number): GLShader {
        return this._renderables[aIndex];
    }

    public initialize() {
    }

    public getLightingType(): number {
        return this._lightingType;
    }

    public initTransformation(aShader: GLShader) {
        if (this._sceneAPI.manipulatorModule.isEnabled()) {
            aShader.addPreprocessorDefinition("#define MANIPULATOR_ENABLED 1");
        }
    }

    public initLogarithmicDepthBuffer(aShader: GLShader) {
        if (this._sceneAPI.renderSettingsModule.getLogarithmicDepthBufferSupportEnabled()) {
            aShader.addPreprocessorDefinition("#define LOGDEPTH 1");
            aShader.attachVertexShader(vsLogDepth);
            aShader.attachFragmentShader(fsLogDepth);
        }
    }

    public initXRay(aShader: GLShader) {
        if (this._sceneAPI.renderSettingsModule.getRenderXRay()) {
            aShader.addPreprocessorDefinition("#define XRAY 1");
            aShader.attachVertexShader(vsXray);
            aShader.attachFragmentShader(fsXray);
        }
    }

    public initLighting(aShader: GLShader) {
        this._lightingType = 2;

        // Defines
        if (this._sceneAPI.lightingModule.getSSAO().isEnabled()) {
            aShader.addPreprocessorDefinition("#define SSAO 1");
        }

        if (this._sceneAPI.shadowsModule.getShadowMapEnabled()) {
            let shadowMapType = this._sceneAPI.shadowsModule.getShadowMapType();
            if (shadowMapType === 'pcf') {
                aShader.addPreprocessorDefinition("#define SHADOW_MAP 1");
            } else if (shadowMapType === 'vsm') {
                aShader.addPreprocessorDefinition("#define SHADOW_MAP_VARIANCE 1");
            }
        }

        if (this._sceneAPI.lightingModule.getFog().isEnabled()) {
            aShader.addPreprocessorDefinition("#define FOG 1");
        }

        // Shaders
        aShader.attachFragmentShader(packToFromRGBA);
        aShader.attachFragmentShader(fsLightingCommon);
        aShader.attachVertexShader(vsLightingCommon);

        if (this._sceneAPI.lightingModule.getFastLightingEnabled()) {
            aShader.attachVertexShader(vsLightingInVS);
            aShader.attachFragmentShader(fsLightingInVS);
        } else {
            aShader.attachVertexShader(vsLighting);
            aShader.attachFragmentShader(fsLighting);
        }
    }

    public initSimpleLighting(aShader: GLShader) {
        this._lightingType = 1;

        if (this._sceneAPI.lightingModule.getFastLightingEnabled()) {
            aShader.attachVertexShader(vsLlightingSimpleInVS);
            aShader.attachFragmentShader(fsLightingSimpleInVS);
        } else {
            aShader.attachVertexShader(vsLightingSimple);
            aShader.attachFragmentShader(fsLightingSimple);
        }
    }

    public initClippingPlane(aShader: GLShader) {
        if (this._sceneAPI.clippingPlaneModule.isEnabled()) {
            aShader.addPreprocessorDefinition("#define CLIPPING_PLANE 1");
            aShader.attachVertexShader(vsCplane);
            aShader.attachFragmentShader(fsCplane);
        }
    }
}
